import React from 'react';

// --- Hashing algorithm for CRI Matrix ---
const stringToCriHash = (str) => {
    if (!str) return Array(16).fill(0);
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
        hash = (hash << 5) - hash + str.charCodeAt(i);
        hash |= 0;
    }
    const octalString = (hash >>> 0).toString(8).padStart(11, '0');
    let finalGrid = Array(16).fill(0);
    for (let i = 0; i < 16; i++) {
        const charIndex = i % octalString.length;
        const seed = (octalString.charCodeAt(charIndex) + i) % 8;
        finalGrid[i] = seed;
    }
    return finalGrid;
};

// --- Radial Engine for TAG Solomonic ---
const TAG_RADIUS = 150;
const TAG_CENTER = { x: 150, y: 150 };
const ALPHANUMERIC_CHARS = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ1234567890';
const SYMBOL_MAP = '◬◮◸◹◺◿✦✧';

const getRadialCoords = (char, radius) => {
    const index = ALPHANUMERIC_CHARS.indexOf(char);
    if (index === -1) return null;
    const angle = (index / 36) * 2 * Math.PI - Math.PI / 2;
    return {
        x: TAG_CENTER.x + radius * Math.cos(angle),
        y: TAG_CENTER.y + radius * Math.sin(angle),
    };
};

const MiniCipher = ({ type, data, decorators = [], size = 40, svgId = "" }) => {
    if (!data) return null;

    // RENDER TAG (SOLOMONIC RADIAL - KALEIDOSCOPE)
    if (type === 'solomon') {
        const inputStr = typeof data === 'string' ? data.toUpperCase() : '';
        const pathStr = inputStr.replace(/[^A-Z0-9]/g, '');

        // Vowel-driven background geometry
        const uniqueVowels = [...new Set(pathStr.match(/[AEIOU]/g) || [])].length;
        let bgGeometry = null;
        if (uniqueVowels === 1) bgGeometry = <circle cx={TAG_CENTER.x} cy={TAG_CENTER.y} r={TAG_RADIUS * 0.4} fill="none" stroke="#dc2626" strokeWidth="1" opacity="0.5" />;
        if (uniqueVowels === 2) bgGeometry = <rect x={TAG_CENTER.x - 60} y={TAG_CENTER.y - 60} width="120" height="120" fill="none" stroke="#dc2626" strokeWidth="1" opacity="0.5" transform={`rotate(45 ${TAG_CENTER.x} ${TAG_CENTER.y})`} />;
        if (uniqueVowels >= 3) {
            const points = Array.from({length: Math.min(uniqueVowels, 8)}).map((_, i) => {
                const angle = (i / Math.min(uniqueVowels, 8)) * 2 * Math.PI - Math.PI / 2;
                return `${TAG_CENTER.x + TAG_RADIUS * 0.9 * Math.cos(angle)},${TAG_CENTER.y + TAG_RADIUS * 0.9 * Math.sin(angle)}`;
            }).join(' ');
            bgGeometry = <polygon points={points} fill="none" stroke="#dc2626" strokeWidth="1" opacity="0.5" />;
        }
        
        let pathSegments = [];
        if (pathStr.length > 1) {
            for (let i = 0; i < pathStr.length - 1; i++) {
                const p1 = getRadialCoords(pathStr[i], TAG_RADIUS * 0.8);
                const p2 = getRadialCoords(pathStr[i+1], TAG_RADIUS * 0.8);
                if(p1 && p2) {
                    const dx = p2.x - p1.x;
                    const dy = p2.y - p1.y;
                    const dist = Math.sqrt(dx*dx + dy*dy);
                    const gap = 12;
                    if(dist > gap * 2) {
                        const ratio = gap / dist;
                        const start = { x: p1.x + dx * ratio, y: p1.y + dy * ratio };
                        const end = { x: p2.x - dx * ratio, y: p2.y - dy * ratio };
                        
                        // Original Line
                        pathSegments.push(`M ${start.x} ${start.y} L ${end.x} ${end.y}`);
                        // KALEIDOSCOPE MIRROR (Mirror across X-axis for perfect symmetry)
                        pathSegments.push(`M ${300 - start.x} ${start.y} L ${300 - end.x} ${end.y}`);
                    }
                }
            }
        }

        return (
            <svg id={svgId} width={size} height={size} viewBox={`0 0 300 300`}>
                <rect x="0" y="0" width="300" height="300" fill="none" stroke="#dc2626" strokeWidth="1" opacity="0.3" />
                <circle cx={TAG_CENTER.x} cy={TAG_CENTER.y} r={TAG_RADIUS} fill="none" stroke="#dc2626" strokeWidth="0.5" opacity="0.3" />
                <circle cx={TAG_CENTER.x} cy={TAG_CENTER.y} r={TAG_RADIUS*0.8} fill="none" stroke="#dc2626" strokeWidth="0.5" opacity="0.3" />
                
                {bgGeometry}

                {/* Transliterated Cryptographic Ring (Safe geometric chars, NO emojis) */}
                {pathStr.split('').map((char, i) => {
                    const angle = (i / pathStr.length) * 360 - 90;
                    const symbolIndex = ALPHANUMERIC_CHARS.indexOf(char);
                    const symbol = SYMBOL_MAP[symbolIndex % SYMBOL_MAP.length];
                    return <text key={i} x={TAG_CENTER.x} y={TAG_CENTER.y - (TAG_RADIUS * 0.9)} transform={`rotate(${angle} ${TAG_CENTER.x} ${TAG_CENTER.y})`} fill="#dc2626" fontSize="12" textAnchor="middle" opacity="0.7">{symbol}</text>;
                })}
                
                {/* The Symmetrical Path */}
                {pathSegments.map((d, i) => <path key={i} d={d} stroke="#dc2626" strokeWidth="2.5" strokeLinecap="round" style={{filter: 'drop-shadow(0 0 4px rgba(220, 38, 38, 0.8))'}} />)}

                {/* Nodes */}
                {pathStr.split('').map((char, i) => {
                    const coords = getRadialCoords(char, TAG_RADIUS * 0.8);
                    if (!coords) return null;
                    return (
                        <g key={i}>
                            <circle cx={coords.x} cy={coords.y} r="3" fill="#0a0a0a" stroke="#dc2626" strokeWidth="1.5" />
                            <circle cx={300 - coords.x} cy={coords.y} r="3" fill="#0a0a0a" stroke="#dc2626" strokeWidth="1.5" />
                        </g>
                    );
                })}

                {/* Pure SVG Decorators in 8 master slots (No Purple Emojis!) */}
                {[
                    {x: 40, y: 40}, {x: 260, y: 40}, {x: 40, y: 260}, {x: 260, y: 260},
                    {x: 150, y: 15}, {x: 285, y: 150}, {x: 150, y: 285}, {x: 15, y: 150}
                ].map((pos, i) => {
                    const dec = decorators[i];
                    if(!dec) return null;
                    return (
                        <g key={i} transform={`translate(${pos.x}, ${pos.y})`} stroke="#dc2626" strokeWidth="2" fill="none" style={{filter: 'drop-shadow(0 0 5px rgba(220, 38, 38, 0.8))'}}>
                            {dec === 'circle' && <circle cx="0" cy="0" r="8" />}
                            {dec === 'square' && <rect x="-7" y="-7" width="14" height="14" />}
                            {dec === 'triangle' && <polygon points="0,-8 8,6 -8,6" />}
                            {dec === 'diamond' && <polygon points="0,-9 7,0 0,9 -7,0" />}
                            {dec === 'moon' && <path d="M 4 -6 A 8 8 0 1 1 -4 6 A 6 6 0 1 0 4 -6" />}
                            {dec === 'star' && <polygon points="0,-9 3,-3 9,-3 4,2 6,9 0,5 -6,9 -4,2 -9,-3 -3,-3" strokeWidth="1.5" />}
                        </g>
                    );
                })}
            </svg>
        );
    }

    // RENDER CRI (MATRIX - MULTI-WORD)
    if (type === 'cipher') {
        const inputStr = typeof data === 'string' ? data.toUpperCase() : '';
        const words = inputStr.split(' ').filter(w => w.length > 0);
        if (words.length === 0) words.push(''); // ensure at least one grid

        const padding = 20;
        const gridWidth = 400;
        const totalWidth = (words.length * gridWidth) + ((words.length - 1) * padding);

        return (
            <svg id={svgId} width={size} height={size} viewBox={`0 0 ${totalWidth} 400`} preserveAspectRatio="xMidYMid meet">
                {words.map((word, wordIndex) => {
                    const matrixData = stringToCriHash(word);
                    const offsetX = wordIndex * (gridWidth + padding);

                    return (
                        <g key={wordIndex} transform={`translate(${offsetX}, 0)`}>
                            {/* Grid Border */}
                            <rect x="0" y="0" width="400" height="400" fill="none" stroke="#003300" strokeWidth="2" />
                            {matrixData.map((state, i) => {
                                if (state === 0) return null;
                                const col = i % 4; const row = Math.floor(i / 4);
                                const x = col * 100; const y = row * 100;
                                const cx = x + 50; const cy = y + 50;
                                const style = {filter: 'drop-shadow(0 0 8px rgba(0, 255, 65, 0.7))'};
                                return (
                                    <g key={i} style={{transformOrigin: `${cx}px ${cy}px`}}>
                                        {state === 1 && <g style={style}><rect x={x+20} y={y+20} width="60" height="60" fill="none" stroke="#00ff41" strokeWidth="6" /><rect x={x+35} y={y+35} width="30" height="30" fill="#00ff41" /></g>}
                                        {state === 2 && <g style={style}><circle cx={cx} cy={cy} r="30" fill="none" stroke="#00ff41" strokeWidth="8" /><path d={`M ${cx-18} ${cy-18} L ${cx+18} ${cy+18} M ${cx-18} ${cy+18} L ${cx+18} ${cy-18}`} stroke="#00ff41" strokeWidth="8" /></g>}
                                        {state === 3 && <g style={style}><polygon points={`${cx},${y+25} ${x+75},${y+65} ${x+25},${y+65}`} fill="none" stroke="#00ff41" strokeWidth="8" /><line x1={x+20} y1={y+80} x2={x+80} y2={y+80} stroke="#00ff41" strokeWidth="8" strokeLinecap="square" /></g>}
                                        {state === 4 && <g style={style}><line x1={x+30} y1={y+20} x2={x+30} y2={y+80} stroke="#00ff41" strokeWidth="10" /><line x1={x+70} y1={y+20} x2={x+70} y2={y+80} stroke="#00ff41" strokeWidth="10" /><circle cx={cx} cy={cy} r="10" fill="#00ff41" /></g>}
                                        {state === 5 && <path d={`M ${x+15} ${cy+20} L ${x+35} ${cy+20} L ${x+35} ${cy-20} L ${x+65} ${cy-20} L ${x+65} ${cy+20} L ${x+85} ${cy+20}`} fill="none" stroke="#00ff41" strokeWidth="8" style={style} />}
                                        {state === 6 && <g style={style}><rect x={x+25} y={y+25} width="50" height="50" fill="none" stroke="#00ff41" strokeWidth="8" /><path d={`M ${x+25} ${y+25} L ${x+75} ${y+75} M ${x+25} ${y+75} L ${x+75} ${y+25}`} stroke="#00ff41" strokeWidth="8" /></g>}
                                        {state === 7 && <g style={style}><polygon points={`${cx},${y+20} ${x+80},${cy} ${cx},${y+80} ${x+20},${cy}`} fill="none" stroke="#00ff41" strokeWidth="8" /><rect x={cx-10} y={cy-10} width="20" height="20" fill="#00ff41" /></g>}
                                    </g>
                                );
                            })}
                        </g>
                    );
                })}
            </svg>
        );
    }

    // RENDER ESO (LEGACY CHALK)
    const SIGIL_NODES = { 0:{cx:150,cy:150}, 1:{cx:150,cy:40}, 2:{cx:245,cy:95}, 3:{cx:245,cy:205}, 4:{cx:150,cy:260}, 5:{cx:55,cy:205}, 6:{cx:55,cy:95} };
    if (type === 'sigil') {
        let pathData = []; 
        try { pathData = typeof data === 'string' ? JSON.parse(data) : data; } catch (e) {}
        
        let d = "";
        if (pathData.length > 0 && SIGIL_NODES[pathData[0].node]) {
            d += `M ${SIGIL_NODES[pathData[0].node].cx} ${SIGIL_NODES[pathData[0].node].cy}`;
            for (let i = 1; i < pathData.length; i++) {
                const p1 = SIGIL_NODES[pathData[i-1].node]; const p2 = SIGIL_NODES[pathData[i].node]; if (!p1 || !p2) continue;
                if (pathData[i].curve) {
                    const dx = p2.cx - p1.cx; const dy = p2.cy - p1.cy; const dist = Math.sqrt(dx*dx + dy*dy);
                    const sweepFlag = pathData[i].curve === 1 ? 1 : 0;
                    d += ` A ${dist * 0.8} ${dist * 0.8} 0 0 ${sweepFlag} ${p2.cx} ${p2.cy}`;
                } else { d += ` L ${p2.cx} ${p2.cy}`; }
            }
        }
        return (
            <svg id={svgId} width={size} height={size} viewBox="0 0 300 300">
                <circle cx="150" cy="150" r="110" fill="none" stroke="#e5e7eb" strokeWidth="1" opacity="0.3" className="ui-element" />
                {Object.values(SIGIL_NODES).map((pos, i) => <circle key={i} cx={pos.cx} cy={pos.cy} r="3" fill="#e5e7eb" opacity="0.4" className="ui-element" />)}
                {d && <path d={d} fill="none" stroke="#e5e7eb" strokeWidth="6" strokeLinecap="round" strokeLinejoin="round" style={{filter: 'drop-shadow(0 0 5px rgba(255,255,255,0.5))'}} />}
            </svg>
        );
    }
    return null;
};

export default MiniCipher;